import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Input, Typography } from 'antd';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Localization from '../../utils/localization';

import './styles.scss';
import EntitiesBrowser from '../EntitiesBrowser';
import { exportConfigActions } from '../../redux/actions';

const { Title } = Typography;

const EditExportTemplateModal = ({
    config,
    editCheckedScenarios,
    editCheckedBusinessCases,
    name,
    description,
    entities,
    businessCases,
    isEditModalOpen,
    handleClose,
}) => {
    const dispatch = useDispatch();
    const [checkedScenarios, setCheckedScenarios] = useState([]);
    const [checkedBusinessCases, setCheckedBusinessCases] = useState([]);
    const [expandedBusinessCases, setExpandedBusinessCases] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [templateIncludeBusinessCase, setTemplateIncludeBusinessCase] = useState(false);
    const [templateOnlyNonCalculatedValues, setTemplateOnlyNonCalculatedValues] = useState(false);

    useEffect(() => {
        setTemplateName(name);
        setTemplateDescription(description);
    }, [setTemplateName, setTemplateDescription, name, description]);

    useEffect(() => {
        const toScenarioId = (n) => {

            if (Number.isInteger(n)) return n;
            const raw = n?.scenario_id ?? n?.scenarioId ?? n?.id;
            const num = typeof raw === 'string' ? Number(raw) : raw;
        return Number.isInteger(num) ? num : undefined;
    };

        const toBusinessCaseId = (bc) => {
        
            if (Number.isInteger(bc)) return bc;
        
            const raw = bc?.business_case_id ?? bc?.id;
            const num = typeof raw === 'string' ? Number(raw) : raw;
        return Number.isInteger(num) ? num : undefined;
    };
    
    setCheckedScenarios(
        (editCheckedScenarios ?? []).map(toScenarioId).filter((x) => x !== undefined)
    );
    
    setCheckedBusinessCases(
        (editCheckedBusinessCases ?? []).map(toBusinessCaseId).filter((x) => x !== undefined)
    );
    }, [editCheckedScenarios, editCheckedBusinessCases]);

    useEffect(() => {
        if (config.result_business_cases != null) {
            setTemplateIncludeBusinessCase(config.result_business_cases);
            setTemplateOnlyNonCalculatedValues(config.non_calculated_only);
        }
    }, [setTemplateIncludeBusinessCase, setTemplateOnlyNonCalculatedValues, config]);

    const handleOk = () => {
        const template = {
            export_config_id: config.export_config_id,
            name: templateName,
            description: templateDescription,
            scenarios: checkedScenarios,
            businessCases: checkedBusinessCases,
            resultBusinessCases: templateIncludeBusinessCase,
            nonCalculatedValuesOnly: templateOnlyNonCalculatedValues,
        };
        dispatch(exportConfigActions.updateExportConfig(template));
        handleCancel();
    };

    const handleCancel = () => {
        handleClose();
    };

    const onCheckScenario = (nodes) => {
        const arr = Array.isArray(nodes) ? nodes : (nodes == null ? [] : [nodes]);
        
        const ids = arr
        .filter(n => n != null)
        .map(n => Number.isInteger(n) ? n : n?.scenario_id ?? n?.scenarioId ?? n?.id)
        .map(x => (typeof x === 'string' ? Number(x) : x))
        .filter(x => Number.isInteger(x));

    setCheckedScenarios(ids);
    };

    const onCheckBusinessCase = (businessCase) => {
        const index = checkedBusinessCases.findIndex((checkedCaseId) => {
            return checkedCaseId === businessCase.business_case_id;
        });

        let newCases = [...checkedBusinessCases];
        if (index === -1) {
            newCases.push(businessCase.business_case_id);
        } else {
            newCases = [...newCases.slice(0, index), ...newCases.slice(index + 1)];
        }
        setCheckedBusinessCases(newCases);
    };

    const renderBusinessCases = (businessCases) => {
        const businessCheckboxes = businessCases.map((businessCase) => {
            return (
                <FormControlLabel
                    key={businessCase.business_case_id}
                    control={
                        <Checkbox
                            checked={checkedBusinessCases.some(
                                (checkedCase) => checkedCase === businessCase.business_case_id,
                            )}
                            onChange={(e) => {
                                onCheckBusinessCase(businessCase);
                                e.stopPropagation();
                            }}
                        />
                    }
                    label={businessCase.name}
                />
            );
        });
        return businessCheckboxes;
    };

    return (
        <Modal
            bodyStyle={{
                minHeight: '45vh',
                display: 'flex',
                flexDirection: 'column',
            }}
            title={Localization.export.exportTemplateModal}
            visible={isEditModalOpen} // not deprecated in our version
            onOk={handleOk}
            confirmLoading={false}
            onCancel={handleCancel}
        >
            <Input
                style={{
                    marginBottom: '10px',
                    maxWidth: '275px',
                    alignSelf: 'left',
                }}
                placeholder={Localization.export.templateName}
                onChange={(e) => {
                    setTemplateName(e.target.value);
                }}
                defaultValue={name}
            />
            <Input
                style={{
                    marginBottom: '25px',
                    maxWidth: '275px',
                    alignSelf: 'left',
                }}
                placeholder={Localization.export.templateDescription}
                onChange={(e) => {
                    setTemplateDescription(e.target.value);
                }}
                defaultValue={description}
            />
            <FormControlLabel
                style={{ marginBottom: '15px' }}
                control={
                    <Checkbox
                        checked={templateIncludeBusinessCase}
                        onChange={() => setTemplateIncludeBusinessCase(!templateIncludeBusinessCase)}
                    />
                }
                label={Localization.export.includeBusinessCaseResult}
            />
            <FormControlLabel
                style={{ marginBottom: '15px' }}
                control={
                    <Checkbox
                        checked={templateOnlyNonCalculatedValues}
                        onChange={() => setTemplateOnlyNonCalculatedValues(!templateOnlyNonCalculatedValues)}
                    />
                }
                label={Localization.export.nonCalculatedValuesOnly}
            />
            <Title level={4} style={{}}>
                Select entities or scenarios:
            </Title>
            {entities ? (
                <EntitiesBrowser
                    entityStructure={entities}
                    handleCheck={onCheckScenario}
                    expandedBusinessCases={expandedBusinessCases}
                    setExpandedBusinessCases={setExpandedBusinessCases}
                    editable={false}
                    checkedNodes={checkedScenarios}
                    style={{ maxHeight: '100px' }}
                />
            ) : (
                'No entities found.'
            )}
            <Title level={4} style={{}}>
                Select business cases:
            </Title>
            {renderBusinessCases(businessCases)}
        </Modal>
    );
};

export default EditExportTemplateModal;
